import { Routes, Route, useLocation } from "react-router-dom";
import "@css/tailwind.css";
import Aside from "@layouts/Aside";
import { useLoginModalOpen } from "@hooks/useLoginModal";
import GlobalHeader from "@layouts/GlobalHeader";
import { routes } from "@routes/routes";
import LoginModal from "@pages/LoginModal";
import { useSession } from "@hooks/useSession";
import LoadingDots from "@components/LoadingDots";
import ProtectedRoute from "@routes/ProtectedRoute";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useStore from "@store/store";

import useRosterPreventRefresh from "@hooks/useRosterPreventRefresh";
import { useLoginUser } from "./hooks/useAuth";
import RosterPolling from "./roster-polling";
import { useGroupSelection } from "./hooks/useGroupSelection";

// 공유 페이지 전용 컴포넌트 (모든 훅 없이 순수하게 렌더링)
function SharePageRoutes() {
  const shareRoutes = routes.filter(
    (route) => route.path.startsWith("/schedule/") && !route.isProtected
  );

  return (
    <Routes>
      {shareRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.element} />
      ))}
    </Routes>
  );
}

// 일반 페이지 컴포넌트 (모든 훅 포함)
function MainApp() {
  const isLoginOpen = useLoginModalOpen();
  const { isAuthenticated } = useStore();
  const queryClient = useQueryClient();
  const loginUser = useLoginUser();
  const groupid = useGroupSelection();

  // 근무표 생성 중 이탈 방지 훅 사용
  useRosterPreventRefresh();

  // 로그아웃 상태가 되면 캐시 초기화 (세션 만료 포함)
  useEffect(() => {
    if (!isAuthenticated) {
      queryClient.removeQueries();
    }
  }, [isAuthenticated, queryClient]);

  // localStorage 기준으로 로그인 이력있으면 유저데이터 확인후 전역처리
  const { isLoading } = useSession();
  // 2. 로딩 상태 체크 (화면 렌더링 차단용)
  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <LoadingDots />
      </div>
    );
  }

  // 그룹관리자는 자신의 그룹인지 체크
  const isGroupAdmin = loginUser?.hn_auth === "HN";
  const isMyGroupId =
    isGroupAdmin && loginUser?.group_id === groupid.selectedGroupId;

  const otherRoutes = routes.filter(
    (route) => !route.path.startsWith("/schedule/") || route.isProtected
  );

  return (
    <>
      {/* RosterMade - Polling */}
      <RosterPolling />

      <div className="c_page__container ">
        {/* Global Header */}
        <GlobalHeader />

        {/* Global Side Bar */}
        <Aside />

        {/* Contents Route */}
        <main className="c_main__container scrollbar-float z-0 relative">
          <Routes>
            {otherRoutes.map((route) => {
              if (route.isProtected) {
                // 그룹 관리자가 아니면 항상 접근 가능, 그룹 관리자인 경우 자신의 그룹일 때만 접근 가능
                if (
                  isGroupAdmin &&
                  !isMyGroupId &&
                  route.path === "/roster_wanted"
                ) {
                  return null;
                }

                // 수간호사
                if (loginUser?.is_head_nurse && route.doNotShowForHeadNurse) {
                  return null;
                }

                // 중첩 라우트가 있는 경우
                if (route.children && route.children.length > 0) {
                  return (
                    <Route key={route.path} element={<ProtectedRoute />}>
                      <Route path={route.path} element={route.element}>
                        {route.children.map((child) =>
                          child.index ? (
                            <Route
                              key={`${route.path}__index`}
                              index
                              element={child.element}
                            />
                          ) : (
                            <Route
                              key={`${route.path}__${child.path}`}
                              path={child.path}
                              element={child.element}
                            />
                          )
                        )}
                      </Route>
                    </Route>
                  );
                }

                return (
                  <Route key={route.path} element={<ProtectedRoute />}>
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  </Route>
                );
              }
              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={route.element}
                />
              );
            })}
          </Routes>

          {/* login Modal */}
          {isLoginOpen && <LoginModal />}
        </main>
      </div>
    </>
  );
}

function App() {
  const location = useLocation();
  const isSharePage = location.pathname.startsWith("/schedule/");

  // 공유하기 라우터
  if (isSharePage) {
    return <SharePageRoutes />;
  }

  return <MainApp />;
}

export default App;
